/**
 * NotificationsScreen — the full-page notification inbox (a roomier sibling of
 * the header bell's popover). Same data, same Redux slice (`notifications`), so
 * it stays live: realtime prepends + the bell's mark-read flow into this view and
 * vice-versa.
 *
 * It reads `state.notifications.items` and reuses `useNotificationsHook` for
 * hydrate / load-older / mark-read / mark-all-read (never calls the api module
 * directly). Filtering (All vs Unread, type, severity, text search) is done
 * client-side over the loaded rows — mirroring how the bell filters — so it never
 * fights the shared slice with server-side filter state. "Load older" paginates
 * the same shared list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import DoneAllOutlined from "@mui/icons-material/DoneAllOutlined";
import NotificationsNoneOutlined from "@mui/icons-material/NotificationsNoneOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import SearchInput from "../../components/inputs/SearchInput";
import NotificationPreferencesDialog from "../../components/notifications/NotificationPreferencesDialog";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import { resolveNotificationLink } from "../../components/notifications/notificationTarget";
import { useNotificationsHook } from "../../hooks/useNotificationsHook";
import { notificationMarkRead } from "../../redux/reducers/notificationsReducer";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { T } from "../SupportScreen/components/supportUi";

// Content column max width — matches the Support/Correspondents admin pages feel
// but narrower since this is a single-stream list.
const MAX_W = 920;

// Severity → accent color (rail + dot + chip). Mirrors the bell.
const SEVERITY_COLOR = {
  high: "#dc2626",
  med: "#f59e0b",
  low: "#64748b",
  info: "#1693b3",
};

const SEVERITY_OPTIONS = [
  { value: "", label: "All severities" },
  { value: "high", label: "High" },
  { value: "med", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "info", label: "Info" },
];

const REALTIME_HINT = {
  unconfigured: "Live updates aren't enabled yet — this inbox refreshes periodically.",
  disconnected: "Reconnecting to live updates…",
  connecting: "Connecting to live updates…",
};

// "loan_changed" → "Loan changed"
const humanizeType = (type = "") =>
  type.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Strip server HTML to plain text for the body line.
const toText = (html) =>
  typeof html === "string" ? html.replace(/<[^>]+>/g, " ").trim() : "";

const timeAgo = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 45) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateTime(iso, "date");
};

// Day bucket label for the group headers (Today / Yesterday / M/d/yyyy), keyed
// off the Eastern calendar date so it lines up with the timestamps we show.
const dayKey = (iso) => formatDateTime(iso, "date") || "—";
const dayLabel = (key) => {
  const today = formatDateTime(new Date().toISOString(), "date");
  const yKey = formatDateTime(
    new Date(Date.now() - 86400000).toISOString(),
    "date",
  );
  if (key === today) return "Today";
  if (key === yKey) return "Yesterday";
  return key;
};

const initials = (nameOrEmail = "") => {
  const s = String(nameOrEmail).split("@")[0];
  const parts = s.trim().split(/\s+|\./).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ── Row ──────────────────────────────────────────────────────────────────────
const NotificationRow = ({ n, onClick }) => {
  const unread = !n.readAt;
  const color = SEVERITY_COLOR[n.severity] || SEVERITY_COLOR.info;
  const body = toText(n.body);
  const actor = n.actor?.name || n.actor?.email;
  const link = resolveNotificationLink(n);

  return (
    <Box
      onClick={() => onClick(n)}
      sx={{
        display: "flex",
        gap: 1.5,
        px: 2.5,
        py: 1.75,
        cursor: "pointer",
        borderLeft: `3px solid ${unread ? color : "transparent"}`,
        background: unread ? "rgba(22,147,179,0.035)" : "#fff",
        borderBottom: `1px solid ${T.line}`,
        transition: "background 0.12s",
        "&:hover": { background: "rgba(15,23,42,0.04)" },
      }}
    >
      {/* Actor avatar / severity dot */}
      <Box
        sx={{
          flexShrink: 0,
          width: 34,
          height: 34,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          fontSize: "11.5px",
          fontWeight: 700,
          color: actor ? "#fff" : color,
          background: actor ? color : `${color}1f`,
          mt: "1px",
        }}
      >
        {actor ? initials(actor) : "•"}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Flx ac jb g={1}>
          <Txt
            component="div"
            sx={{
              fontSize: 13.5,
              fontWeight: unread ? 700 : 600,
              color: T.ink900,
              overflowWrap: "anywhere",
            }}
          >
            {n.title || humanizeType(n.type)}
          </Txt>
          <Tooltip title={formatDateTime(n.createdAt, "datetime")} arrow>
            <Txt sx={{ fontSize: 11, color: T.ink300, flexShrink: 0, whiteSpace: "nowrap" }}>
              {timeAgo(n.createdAt)}
            </Txt>
          </Tooltip>
        </Flx>

        {body && (
          <Txt
            component="div"
            sx={{
              mt: 0.4,
              fontSize: 12.5,
              color: T.ink500,
              lineHeight: 1.5,
              overflowWrap: "anywhere",
            }}
          >
            {body}
          </Txt>
        )}

        <Flx ac g={1} sx={{ mt: 0.85, flexWrap: "wrap" }}>
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              color,
              background: `${color}14`,
              borderRadius: "5px",
              px: 0.75,
              py: "2px",
            }}
          >
            {humanizeType(n.type)}
          </Box>
          {actor && (
            <Txt sx={{ fontSize: 11.5, color: T.ink300 }}>{actor}</Txt>
          )}
          {link && (
            <Txt
              onClick={(e) => {
                e.stopPropagation();
                onClick(n);
              }}
              sx={{
                fontSize: 11.5,
                fontWeight: 600,
                color: T.teal600,
                ml: "auto",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {link.label} →
            </Txt>
          )}
        </Flx>
      </Box>
    </Box>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────
const NotificationsScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAdmin } = usePermissionsHook();
  const { items, unread, loaded, hasMore, realtime } = useSelector(
    (s) => s.notifications,
  );
  const { hydrateInbox, loadMore, markRead, markAllRead } =
    useNotificationsHook();

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [search, setSearch] = useState("");
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Refresh on mount so a deep-link/navigation lands on a current list.
  useEffect(() => {
    hydrateInbox();
  }, [hydrateInbox]);

  // Distinct types present in the loaded rows → the type dropdown options.
  const typeOptions = useMemo(() => {
    const set = new Set();
    items.forEach((n) => n.type && set.add(n.type));
    return Array.from(set).sort();
  }, [items]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((n) => {
      if (showUnreadOnly && n.readAt) return false;
      if (typeFilter && n.type !== typeFilter) return false;
      if (severityFilter && (n.severity || "info") !== severityFilter)
        return false;
      if (q) {
        const hay = `${n.title || ""} ${toText(n.body)} ${
          n.actor?.name || n.actor?.email || ""
        } ${humanizeType(n.type)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, showUnreadOnly, typeFilter, severityFilter, search]);

  // Group the visible rows by Eastern calendar day, newest day first. `items` is
  // already newest-first so the buckets stay ordered as we walk them.
  const groups = useMemo(() => {
    const out = [];
    const index = new Map();
    visible.forEach((n) => {
      const key = dayKey(n.createdAt);
      if (!index.has(key)) {
        index.set(key, out.length);
        out.push({ key, label: dayLabel(key), rows: [] });
      }
      out[index.get(key)].rows.push(n);
    });
    return out;
  }, [visible]);

  const markReadOnce = useCallback(
    (n) => {
      if (!n.readAt) {
        dispatch(notificationMarkRead(n._id));
        markRead(n._id);
      }
    },
    [dispatch, markRead],
  );

  const handleRowClick = useCallback(
    (n) => {
      markReadOnce(n);
      const link = resolveNotificationLink(n, { isAdmin });
      if (link) navigate(link.to);
    },
    [markReadOnce, navigate, isAdmin],
  );

  const handleLoadMore = useCallback(async () => {
    const oldest = items[items.length - 1];
    if (!oldest) return;
    setLoadingMore(true);
    await loadMore(oldest._id);
    setLoadingMore(false);
  }, [items, loadMore]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await hydrateInbox({ silent: true });
    setRefreshing(false);
  }, [hydrateInbox]);

  const filtersActive = Boolean(
    showUnreadOnly || typeFilter || severityFilter || search.trim(),
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        bgcolor: T.bg,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: MAX_W,
          mx: "auto",
          px: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Flx
          ac
          jb
          g={2}
          sx={{ flex: "0 0 auto", pt: 3, pb: 2, flexWrap: "wrap" }}
        >
          <Box>
            <Txt sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em", color: T.ink900 }}>
              Notifications
            </Txt>
            <Txt sx={{ mt: 0.5, fontSize: 13, color: T.ink500 }}>
              {unread > 0
                ? `${unread} unread · everything that's happened across your loans`
                : "You're all caught up"}
            </Txt>
          </Box>
          <Flx ac g={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRounded sx={{ fontSize: 18 }} />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                color: T.ink700,
                borderColor: T.line,
                background: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { borderColor: T.ink300, background: "#f8fafb" },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DoneAllOutlined sx={{ fontSize: 18 }} />}
              onClick={markAllRead}
              disabled={unread === 0}
              sx={{
                color: T.ink700,
                borderColor: T.line,
                background: "#fff",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { borderColor: T.ink300, background: "#f8fafb" },
              }}
            >
              Mark all read
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SettingsOutlined sx={{ fontSize: 18 }} />}
              onClick={() => setPrefsOpen(true)}
              sx={{
                background: T.teal600,
                "&:hover": { background: "#127f97" },
                fontWeight: 600,
                borderRadius: "8px",
              }}
            >
              Settings
            </Button>
          </Flx>
        </Flx>

        {/* Filter bar */}
        <Flx
          ac
          g={1}
          sx={{ flex: "0 0 auto", pb: 1.5, flexWrap: "wrap" }}
        >
          {/* All / Unread segmented switcher */}
          <Box
            sx={{
              display: "inline-flex",
              background: "#eef3f5",
              border: `1px solid ${T.line}`,
              borderRadius: "9px",
              p: "3px",
            }}
          >
            {[
              { key: false, label: "All" },
              { key: true, label: `Unread${unread ? ` (${unread})` : ""}` },
            ].map((opt) => {
              const active = showUnreadOnly === opt.key;
              return (
                <Box
                  key={String(opt.key)}
                  onClick={() => setShowUnreadOnly(opt.key)}
                  sx={{
                    cursor: "pointer",
                    px: 1.75,
                    py: 0.6,
                    borderRadius: "7px",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: active ? T.ink900 : T.ink500,
                    background: active ? "#fff" : "transparent",
                    boxShadow: active ? "0 1px 2px rgba(22,36,43,.12)" : "none",
                    transition: "all 0.12s",
                    "&:hover": { color: T.ink900 },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>

          <TextField
            select
            size="small"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 180, background: "#fff", borderRadius: 1 }}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>
              All types
            </MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                {humanizeType(t)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            sx={{ minWidth: 150, background: "#fff", borderRadius: 1 }}
          >
            {SEVERITY_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value} sx={{ fontSize: 13 }}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>

          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search notifications…"
            size="small"
            rounded
            variant="paper"
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Flx>

        {/* Realtime status hint */}
        {REALTIME_HINT[realtime] && (
          <Box
            sx={{
              flex: "0 0 auto",
              mb: 1.5,
              px: 1.75,
              py: 0.85,
              background: "#fffbeb",
              border: "1px solid #fef3c7",
              borderRadius: "8px",
            }}
          >
            <Txt sx={{ fontSize: 11.5, color: "#92400e" }}>
              {REALTIME_HINT[realtime]}
            </Txt>
          </Box>
        )}

        {/* List */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            mb: 3,
            border: `1px solid ${T.line}`,
            borderRadius: "14px",
            background: "#fff",
            boxShadow: T.shadow,
          }}
        >
          {!loaded ? (
            <Flx center sx={{ py: 8 }}>
              <CircularProgress size={26} />
            </Flx>
          ) : visible.length === 0 ? (
            <Flx column center sx={{ py: 9, px: 3, textAlign: "center" }}>
              <NotificationsNoneOutlined
                sx={{ fontSize: 40, color: T.ink300, mb: 1.5 }}
              />
              <Txt sx={{ fontSize: 14, fontWeight: 600, color: T.ink700 }}>
                {filtersActive
                  ? "No notifications match these filters"
                  : "You're all caught up"}
              </Txt>
              <Txt sx={{ mt: 0.5, fontSize: 12.5, color: T.ink300 }}>
                {filtersActive
                  ? "Try clearing a filter to see more."
                  : "New activity on your loans will show up here."}
              </Txt>
            </Flx>
          ) : (
            <>
              {groups.map((group) => (
                <Box key={group.key}>
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      px: 2.5,
                      py: 0.85,
                      background: "#f8fafc",
                      borderBottom: `1px solid ${T.line}`,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: T.ink300,
                    }}
                  >
                    {group.label}
                  </Box>
                  {group.rows.map((n) => (
                    <NotificationRow key={n._id} n={n} onClick={handleRowClick} />
                  ))}
                </Box>
              ))}

              {/* Load older — only meaningful on the full (unfiltered) stream,
                  since filtering is client-side over what's loaded. */}
              {hasMore && (
                <Flx center sx={{ py: 1.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    sx={{ fontSize: 12.5, textTransform: "none", color: T.teal600 }}
                  >
                    {loadingMore ? "Loading…" : "Load older"}
                  </Button>
                </Flx>
              )}
            </>
          )}
        </Box>
      </Box>

      <NotificationPreferencesDialog
        open={prefsOpen}
        onClose={() => setPrefsOpen(false)}
      />
    </Box>
  );
};

export default NotificationsScreen;
